import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail, Key, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = email, 2 = code, 3 = success
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Bitte E-Mail eingeben");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, { email });
      toast.success("Falls die E-Mail existiert, wurde ein Code gesendet");
      setStep(2);
    } catch (error) {
      console.error("Reset request error:", error);
      toast.error("Fehler beim Anfordern des Codes");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    
    if (!code || !newPassword) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password/confirm`, {
        token: code,
        new_password: newPassword
      });
      toast.success("Passwort erfolgreich geändert!");
      setStep(3);
    } catch (error) {
      console.error("Reset confirm error:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Fehler beim Zurücksetzen des Passworts");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-yellow-500 rounded-sm flex items-center justify-center">
              <span className="text-black font-black text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>U</span>
            </div>
            <span className="text-xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Unfallmelde Tool
            </span>
          </div>
          
          <h1 className="text-2xl font-bold text-zinc-50 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Passwort zurücksetzen
          </h1>
          <p className="text-zinc-500 text-sm">
            {step === 1 && "Geben Sie Ihre E-Mail-Adresse ein"}
            {step === 2 && "Geben Sie den Code aus der E-Mail ein"}
            {step === 3 && "Passwort erfolgreich geändert!"}
          </p>
        </div>

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={handleRequestReset} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                E-Mail
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  data-testid="reset-email"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 pl-10"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                "Code anfordern"
              )}
            </Button>
            
            <div className="text-center">
              <Link 
                to="/login" 
                className="text-sm text-zinc-500 hover:text-yellow-500 inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zum Login
              </Link>
            </div>
          </form>
        )}

        {/* Step 2: Code & New Password */}
        {step === 2 && (
          <form onSubmit={handleConfirmReset} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 space-y-6">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-3 text-sm text-yellow-500">
              Ein Code wurde an <strong>{email}</strong> gesendet. 
              Bitte überprüfen Sie auch Ihren Spam-Ordner.
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="code" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                Code (erste 8 Zeichen)
              </Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                maxLength={8}
                data-testid="reset-code"
                className="bg-zinc-950 border-zinc-800 text-zinc-50 text-center text-xl tracking-widest font-mono uppercase"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                Neues Passwort
              </Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mindestens 8 Zeichen"
                  data-testid="reset-new-password"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                Passwort bestätigen
              </Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  data-testid="reset-confirm-password"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 pl-10"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-confirm-submit"
              className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird geändert...
                </>
              ) : (
                "Passwort ändern"
              )}
            </Button>
            
            <div className="text-center">
              <button 
                type="button"
                onClick={() => setStep(1)} 
                className="text-sm text-zinc-500 hover:text-yellow-500 inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Andere E-Mail eingeben
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-center space-y-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            
            <div>
              <h2 className="text-xl font-bold text-zinc-50 mb-2">Passwort geändert!</h2>
              <p className="text-zinc-400 text-sm">
                Ihr Passwort wurde erfolgreich zurückgesetzt. 
                Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
              </p>
            </div>
            
            <Button
              onClick={() => navigate("/login")}
              data-testid="goto-login"
              className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
            >
              Zum Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
